import type {
  DashboardContentLinkRef,
  DashboardContentLinkVisibility,
  DashboardContentRelation,
  DashboardContentTagInput,
} from "@/api/contentLinks";
import {
  ContentLinkRef,
  ContentLinkVisibility,
  ContentRelation,
  TaggedContentSettings,
} from "@/types";

/** REST link ref → domain link ref (drops the wire-only `createdAt`). */
export const mapToContentLinkRef = (link: DashboardContentLinkRef): ContentLinkRef => ({
  kind: link.kind,
  entityType: link.entityType,
  entityId: link.entityId,
  title: link.title ?? null,
  logo: link.logo ?? null,
  visibility: link.visibility,
});

/**
 * A response without `relation` is labelled as owned content — a display-only
 * default. Editability never follows from it: `canEdit` is the single
 * server-computed gate and fails closed when absent.
 */
export const mapToContentRelation = (
  relation: DashboardContentRelation | undefined
): ContentRelation => relation ?? "owned";

export const mapToContentLinkVisibility = (
  visibility: DashboardContentLinkVisibility | null | undefined
): ContentLinkVisibility | null => visibility ?? null;

/**
 * Domain visibility → the wire enum. The two vocabularies coincide today; the
 * mapper keeps the boundary explicit so a wire rename stays a one-line change.
 */
export const mapToApiContentLinkVisibility = (
  visibility: ContentLinkVisibility
): DashboardContentLinkVisibility => visibility;

/** Domain tag selection → the `PUT …/tags` request shape. */
export const mapToApiContentTag = ({
  entityType,
  entityId,
}: Pick<ContentLinkRef, "entityType" | "entityId">): DashboardContentTagInput => ({
  entityType,
  entityId,
});

/**
 * The four per-kind tagged-content settings the backend stores but the
 * checked-in Orval client does not describe yet. `yarn rest:codegen` needs a
 * reachable backend, so this interface is the **only** place in the app that
 * assumes their wire shape — both the read (`mapToTaggedContentSettings`) and
 * the write (`mapToApiTaggedContentSettings`) below go through it, and every
 * caller widens its generated DTO with {@link WithTaggedContentSettings}.
 *
 * Every field is optional on purpose: against a backend that has not shipped
 * them yet the reads fall back to today's permissive behaviour.
 *
 * TODO(codegen): once `yarn rest:codegen` has been re-run against a backend
 * exposing `allowNewsTagging`, `defaultTaggedNewsVisibility`,
 * `allowGalleryTagging` and `defaultTaggedGalleryVisibility` on the dashboard
 * federation/league detail + create/update DTOs, delete this interface and
 * `WithTaggedContentSettings`, drop the `?? …` fallbacks in
 * `mapToTaggedContentSettings`, have every `With…<Dashboard…>` alias collapse
 * back to the generated type, and add the four keys to
 * `LEAGUE_AUDIT_FIELD_LABELS` / `ORGANIZATION_AUDIT_FIELD_LABELS` (they are
 * `Record<keyof DashboardUpdate…>` maps, so the keys only become addable then).
 */
export type DashboardTaggedContentSettings = {
  allowNewsTagging?: boolean;
  defaultTaggedNewsVisibility?: DashboardContentLinkVisibility;
  allowGalleryTagging?: boolean;
  defaultTaggedGalleryVisibility?: DashboardContentLinkVisibility;
};

/** The single widening point for {@link DashboardTaggedContentSettings}. */
export type WithTaggedContentSettings<TDto> = TDto & DashboardTaggedContentSettings;

/**
 * REST tagged-content settings → domain settings, read defensively: a missing
 * switch means "tagging allowed" and a missing visibility means `public`, so the
 * UI degrades to the pre-split permissive behaviour against a backend that does
 * not serve the fields yet.
 */
export const mapToTaggedContentSettings = (
  dto: DashboardTaggedContentSettings
): TaggedContentSettings => ({
  allowNewsTagging: dto.allowNewsTagging ?? true,
  defaultTaggedNewsVisibility: dto.defaultTaggedNewsVisibility ?? "public",
  allowGalleryTagging: dto.allowGalleryTagging ?? true,
  defaultTaggedGalleryVisibility: dto.defaultTaggedGalleryVisibility ?? "public",
});

/**
 * The legacy single-value `defaultTaggedContentVisibility` column, derived from
 * the per-kind settings. The backend still reads it, so create/update keep
 * sending it.
 *
 * Rule: `public` when at least one **taggable** kind defaults to `public` —
 * i.e. the entity is willing to show some tagged content publicly straight
 * away — and `hidden` otherwise (both kinds hidden, or tagging switched off).
 * A kind whose switch is off contributes nothing: it can have no new tags to
 * make public.
 */
export const mapToApiLegacyTaggedContentVisibility = (
  settings: TaggedContentSettings
): DashboardContentLinkVisibility =>
  (settings.allowNewsTagging && settings.defaultTaggedNewsVisibility === "public") ||
  (settings.allowGalleryTagging && settings.defaultTaggedGalleryVisibility === "public")
    ? "public"
    : "hidden";

/**
 * Domain tagged-content settings → the create/update body fields, legacy
 * `defaultTaggedContentVisibility` included (see
 * {@link mapToApiLegacyTaggedContentVisibility}).
 */
export const mapToApiTaggedContentSettings = (
  settings: TaggedContentSettings
): DashboardTaggedContentSettings & {
  defaultTaggedContentVisibility: DashboardContentLinkVisibility;
} => ({
  allowNewsTagging: settings.allowNewsTagging,
  defaultTaggedNewsVisibility: mapToApiContentLinkVisibility(settings.defaultTaggedNewsVisibility),
  allowGalleryTagging: settings.allowGalleryTagging,
  defaultTaggedGalleryVisibility: mapToApiContentLinkVisibility(
    settings.defaultTaggedGalleryVisibility
  ),
  defaultTaggedContentVisibility: mapToApiLegacyTaggedContentVisibility(settings),
});
